import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./Header.module.scss";
import RefreshIcon from "../../icons/refresh.svg";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuActive } from "../../redux/layoutStore";
import { updateLoggedIn } from "../../redux/authStore";
import { useNavigate, useParams } from "react-router-dom";
import { showModal } from "../../redux/modalStore";
import Button from "../../components/Button/Button";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import { getOrganizationData } from "../../actions/organization";
import { setLoadingContext } from "../../redux/loaderStore";
import { REFRESH_BUTTON_LOADER } from "../../constants/loader";
import axiosInstance from "../../utils/utils";
import { setUserData } from "../../redux/userStore";
import { useTranslation } from "react-i18next";
import { PanelLeftClose, PanelLeftOpen, LogOut, User2, Plus } from "lucide-react";
import OrgSwitcher from "./OrgSwitcher";
import {
  clearLoginUiState,
  clearOrgContext,
  clearSessionToken,
} from "../../utils/auth";

const REFRESH_INTERVAL = 12 * 60 * 60 * 1000;

const setSharedCookie = (name, val) => {
  const isLocal = window.location.hostname === "localhost";
  const cookieDomain = isLocal ? "" : "domain=.border.ly;";
  document.cookie = `${name}=${val}; ${cookieDomain} path=/; secure; samesite=None`;
};

const Header = () => {
  const menuActive = useSelector((state) => state.layout.menuActive);
  const user = useSelector((state) => state.user.user);
  const userEmail = useSelector((state) => state.user.email);
  const { t } = useTranslation();

  const [backgroundColor, setBackgroundColor] = useState(null);
  const [hasTransparentBackground, setHasTransparentBackground] =
    useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLogoError, setIsLogoError] = useState(false);
  const headerRef = useRef(null);

  const { orgId: orgIdFromParams } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionToken = localStorage.getItem("session_token");

  const resolvedOrgId = orgIdFromParams || localStorage.getItem("org_id") || "";

  const modules = useSelector((state) => state.user.modules[orgIdFromParams]);
  const currentOrganization = useSelector(
    (state) => state.user.organizations[orgIdFromParams],
  );

  const logoUrl = currentOrganization?.Logo?.url;

  const updateHeaderHeight = useCallback(() => {
    if (!headerRef.current) return;
    const height = headerRef.current.getBoundingClientRect().height;
    if (!height) return;
    document.documentElement.style.setProperty(
      "--header-height",
      `${Math.round(height)}px`,
    );
  }, []);

  useEffect(() => {
    if (!orgIdFromParams) return;
    localStorage.setItem("org_id", orgIdFromParams);
    setSharedCookie("org_id", orgIdFromParams);
  }, [orgIdFromParams]);

  // Function to check if background from organization logo is transparent and if not, find background color;
  useEffect(() => {
    if (logoUrl) {
      const img = new Image();
      img.src = logoUrl;
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let transparentPixelCount = 0;
        let opaquePixelCount = 0;
        const colorCount = {};
        let maxCount = 0;
        let dominantColor = null;

        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3];

          if (alpha < 255) {
            transparentPixelCount++;
          } else {
            opaquePixelCount++;
            const color = `${pixels[i]},${pixels[i + 1]},${pixels[i + 2]}`;
            colorCount[color] = (colorCount[color] || 0) + 1;

            if (colorCount[color] > maxCount) {
              maxCount = colorCount[color];
              dominantColor = color;
            }
          }
        }

        const totalPixels = transparentPixelCount + opaquePixelCount;
        const transparencyRatio = transparentPixelCount / totalPixels;

        const transparencyThreshold = 0.1;

        setHasTransparentBackground(transparencyRatio > transparencyThreshold);
        setBackgroundColor(
          transparencyRatio > transparencyThreshold
            ? "#FFFFFF"
            : `rgb(${dominantColor})`,
        );
        setImageLoaded(true);
        setIsLogoError(false);
      };

      img.onerror = () => {
        console.error("Failed to load logo image");
        setIsLogoError(true);
      };
    }
  }, [logoUrl]);

  const toggleMenuActive = () => {
    dispatch(updateMenuActive(!menuActive));
  };

  const handleOpenNewFindingModal = () => {
    dispatch(
      showModal({
        name: "new_finding_modal",
      }),
    );
  };

  const handleLogOut = () => {
    clearSessionToken(); // clears LS + cookie
    clearOrgContext(); // clears org_id LS + cookie
    clearLoginUiState(); // /login shows the email form again
    dispatch(updateLoggedIn(false));
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const checkAndRefreshData = () => {
      const lastRefresh = localStorage.getItem("last_refresh_timestamp");
      const now = Date.now();

      if (!lastRefresh || now - lastRefresh > REFRESH_INTERVAL) {
        handleRefreshData();
        localStorage.setItem("last_refresh_timestamp", now);
      }
    };

    // Run the check on component mount
    checkAndRefreshData();

    // Set an interval to run the check every 12 hours
    const interval = setInterval(checkAndRefreshData, REFRESH_INTERVAL);

    // Clear the interval on component unmount
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [updateHeaderHeight]);

  useEffect(() => {
    updateHeaderHeight();
  }, [
    updateHeaderHeight,
    imageLoaded,
    menuActive,
    currentOrganization?.Name,
    user?.Role,
  ]);

  const handleRefreshData = () => {
    if (!orgIdFromParams) return;

    dispatch(
      setLoadingContext({
        context: REFRESH_BUTTON_LOADER,
        isLoading: true,
      }),
    );

    dispatch(
      getOrganizationData(orgIdFromParams, sessionToken, navigate, false),
    )
      .then(() => {
        const refreshQuery = "&refresh=true";
        const options = {
          method: "GET",
          url: `/auth/session?session_token=${sessionToken}${refreshQuery}`,
        };

        return axiosInstance
          .request(options)
          .then((response) => {
            dispatch(setUserData(response.data));
            localStorage.setItem("last_refresh_timestamp", Date.now());
          })
          .catch(() => {
            dispatch(showModal({ name: "error_modal" }));
          });
      })
      .finally(() => {
        dispatch(
          setLoadingContext({
            context: REFRESH_BUTTON_LOADER,
            isLoading: false,
          }),
        );
      });
  };

  const showOrgLogo = logoUrl && !isLogoError && imageLoaded;

  return (
    <div className={styles.wrapper} ref={headerRef}>
      <div className={styles.leftSide}>
        <button
          type="button"
          className={styles.sidebarToggle}
          onClick={toggleMenuActive}
          aria-label={t("header.menuIconAlt")}
        >
          {menuActive ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
        <div className={styles.logoGroup}>
          {showOrgLogo && (
            <div
              className={styles.imageWrapper}
              style={{
                backgroundColor: hasTransparentBackground
                  ? "#FFFFFF"
                  : backgroundColor,
              }}
            >
              <img
                src={logoUrl}
                alt={t("header.orgLogoAlt")}
                className={styles.orgLogo}
              />
            </div>
          )}
        </div>
        <div className={styles.orgSwitcherWrapper}>
          <OrgSwitcher />
        </div>
      </div>
      <div className={styles.rightSide}>
        <Button
          text={t("header.refreshData")}
          icon={RefreshIcon}
          className={styles.refresh}
          onClick={handleRefreshData}
        />
        <Button
          text={t("header.newFinding")}
          className={styles.newFinding}
          icon={<Plus size={16} />}
          onClick={handleOpenNewFindingModal}
        />
        <div className={styles.languageSwitcherWrapper}>
          <LanguageSwitcher />
        </div>
        <div className={styles.userInfo}>
          <User2 size={18} />
          <span className={styles.email}>{userEmail}</span>
        </div>
        <button
          type="button"
          className={styles.signOut}
          onClick={handleLogOut}
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Header;
